import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { resetPassword } from "../../../services/authService";
import IconButton from "../../common/Button";
import TextField from "../../common/TextField";
import PasswordStrength from "../../common/PasswordStrength";
import ChipStatus from "../../common/ChipStatus";
import Spacer from "../../common/Spacer";
import FullScreenOverlay from "../../common/FullScreenOverlay";

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [passwordRulesFulfilled, setPasswordRulesFulfilled] = useState(false);
  // Extract uid and token from the URL
  const { uid, token } = useParams();
  const [searchParams] = useSearchParams();
  // Check if it's the first time the user is resetting the password which means after the user is created freshly
  const isFirstTime = searchParams.get("first_time") === "true";

  const navigate = useNavigate();

  const handleBackToLogin = () => {
    navigate("/login", { replace: true });
  };

  const handlePasswordChange = (password) => {
    setNewPassword(password);
  };

  const handleRulesFulfillment = (rulesFulfilled) => {
    setPasswordRulesFulfilled(rulesFulfilled.every(Boolean));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!passwordRulesFulfilled) {
      setError("Ensure password meets criteria.");
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    try {
      await resetPassword(uid, token, newPassword);
      setError("");
      setSuccessMessage("Password updated successfully");
    } catch (error) {
      setSuccessMessage("");
      // Handle the error
      setError(
        error.response?.data?.error || "An error occurred. Please try again."
      );
    }
  };

  return (
    <>
      <form className="login-form" onSubmit={onSubmit}>
        <div className="login-title h4-bold">Set new password</div>
        <Spacer />
        {isFirstTime ? (
          <p>Please enter and confirm your new password.</p>
        ) : (
          <p>
            Please enter and confirm your new password to reset your password.
          </p>
        )}

        <div className="login-form-fields">
          <div className="form-group">
            <label htmlFor="email" className="field-label">
              New password
            </label>
            <PasswordStrength
              value={newPassword}
              onChange={(e) => handlePasswordChange(e.target.value)}
              onRulesFulfilled={handleRulesFulfillment}
            />
          </div>
          <Spacer />
          <div className="form-group">
            <label htmlFor="confirmPassword" className="field-label">
              Confirm new password
            </label>
            <TextField
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              state={error ? "error" : "default"}
              required
              maxLength={64}
            />
          </div>
        </div>

        {/* Use the Button component as the submit button */}
        <IconButton
          variant="primary"
          state="idle"
          icon=""
          type="submit"
          width="376px"
        >
          {isFirstTime ? "Set my password" : "Reset my password"}
        </IconButton>
        <Spacer height="25px" />
        <IconButton
          variant="ghost"
          state="idle"
          icon=""
          type="button"
          width="376px"
          onClick={handleBackToLogin}
        >
          Back to login
        </IconButton>
        {/* Display error if any */}
        {error && <ChipStatus status="danger">{error}</ChipStatus>}
        {/* Display success message if any */}
        {successMessage && (
          <ChipStatus status="success">{successMessage}</ChipStatus>
        )}
      </form>

      {successMessage && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          popupType="save"
          buttons={[
            {
              label: "Go to login",
              onClick: () => handleBackToLogin(),
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">
            {isFirstTime
              ? "Password set successfull"
              : "Password reset successfull"}
          </h5>
          <p>
            {isFirstTime
              ? "Your password has been set successfully. You can now login using your password."
              : "Your password has been reset successfully. You can now login using your new password."}
          </p>
        </FullScreenOverlay>
      )}
    </>
  );
};

export default ResetPassword;
